import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Truck, Plus, Search, Filter, Download, Upload, ChevronLeft, ChevronRight,
  MoreVertical, Eye, Edit, Trash2, CheckCircle, XCircle, AlertTriangle,
  Package, Database, Mail, Phone, MapPin, CreditCard, Loader2, Shield,
  Building, User, Activity, DollarSign
} from 'lucide-react'
import { cn, formatCurrency, formatNumber, formatDate } from '../../lib/utils'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { purchasesApi, queryKeys } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Modal } from '../../components/ui/Modal'
import { Dropdown } from '../../components/ui/Dropdown'
import { Skeleton } from '../../components/ui/Skeleton'
import { Table } from '../../components/ui/Table'
import { Pagination } from '../../components/ui/Pagination'
import { useUIStore } from '../../stores/uiStore'
import { useAuthStore , usePermissions } from '../../stores/authStore'
import toast from 'react-hot-toast'

export default function SuppliersPage() {
  const { hasPermission } = usePermissions()
  const { modals, openModal, closeModal } = useUIStore()
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<SupplierListParams>({
    page: 1,
    page_size: 20,
    sort_by: 'name',
    sort_order: 'asc',
  })
  const [deletedId, setDeletedId] = useState<string | null>(null)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.suppliers(filters),
    queryFn: () => purchasesApi.listSuppliers(filters),
    placeholderData: (prev) => prev,
  })

  const createMutation = useMutation({
    mutationFn: (data: any) => purchasesApi.createSupplier(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers() })
      toast.success('تأمین‌کننده ایجاد شد')
      closeModal('create-supplier')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'ایجاد ناموفق بود'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => purchasesApi.updateSupplier(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers() })
      toast.success('تأمین‌کننده به‌روزرسانی شد')
      closeModal('edit-supplier')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'به‌روزرسانی ناموفق بود'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => purchasesApi.deleteSupplier(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers() })
      toast.success('تأمین‌کننده حذف شد')
      closeModal('delete-confirm')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'حذف ناموفق بود'),
  })

  const handlePageChange = (page: number) => setFilters(prev => ({ ...prev, page }))
  const handlePageSizeChange = (size: number) => setFilters(prev => ({ ...prev, page_size: size, page: 1 }))
  const handleSearch = (e: React.FormEvent) => { e.preventDefault(); setFilters(prev => ({ ...prev, page: 1 })) }

  const totalPages = data?.total_pages || 1
  const totalItems = data?.total || 0

  const getRowDropdownItems = (supplier: any) => [
    { label: 'مشاهده', icon: Eye, onClick: () => window.open(`/purchase/suppliers/${supplier.id}`, '_blank') },
    { label: 'ویرایش', icon: Edit, onClick: () => openModal('edit-supplier'), disabled: !hasPermission('supplier', 'update') },
    { type: 'divider' as const },
    { label: 'سفارش‌های خرید', icon: Package, onClick: () => {} },
    { label: 'بیل‌ها', icon: DollarSign, onClick: () => {} },
    { label: 'تراکنش‌ها', icon: Activity, onClick: () => {} },
    { type: 'divider' as const },
    { label: 'حذف', icon: Trash2, className: 'text-danger-600', onClick: () => { setDeletedId(supplier.id); openModal('delete-confirm') }, disabled: !hasPermission('supplier', 'delete') },
  ]

  const statusConfig = {
    active: { label: 'فعال', variant: 'success' as const, icon: Activity },
    inactive: { label: 'غیرفعال', variant: 'neutral' as const, icon: XCircle },
    blocked: { label: 'مسدود', variant: 'danger' as const, icon: Shield },
  }

  return (
    <div className="p-6 space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">تأمین‌کنندگان</h1>
          <p className="text-neutral-500 dark:text-neutral-400 mt-1">
            {totalItems.toLocaleString('fa-IR')} تأمین‌کننده ثبت شده
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => openModal('create-supplier')} disabled={!hasPermission('supplier', 'create')}>
            <Plus className="w-4 h-4" />
            تأمین‌کننده جدید
          </Button>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card p-4"
      >
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <Input placeholder="جستجو (نام، کد، ایمیل، تلفن)..." className="flex-1 max-w-md" />
          <Select
            value={filters.status || ''}
            onChange={(v) => setFilters(prev => ({ ...prev, status: v || undefined, page: 1 }))}
            options={[
              { value: '', label: 'همه وضعیت‌ها' },
              { value: 'active', label: 'فعال' },
              { value: 'inactive', label: 'غیرفعال' },
              { value: 'blocked', label: 'مسدود' },
            ]}
            placeholder="وضعیت"
            className="w-36"
          />
          <Dropdown
            trigger={<Button variant="outline"><Filter className="w-4 h-4 ml-1" /> فیلترها</Button>}
            items={[
              { label: 'خروجی اکسل', icon: Download, onClick: () => {} },
              { label: 'وارد کردن', icon: Upload, onClick: () => {} },
            ]}
          />
        </form>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card overflow-hidden"
      >
        {isLoading ? (
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>نام</th>
                    <th className="hidden sm:table-cell">کد</th>
                    <th className="hidden md:table-cell">نوع</th>
                    <th className="hidden lg:table-cell">شهر</th>
                    <th className="hidden xl:table-cell">اطلاعات تماس</th>
                    <th>وضعیت</th>
                    <th className="hidden lg:table-cell">اعتبار</th>
                    <th className="w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      <td><Skeleton variant="text" width="150px" /></td>
                      <td className="hidden sm:table-cell"><Skeleton variant="text" width="80px" /></td>
                      <td className="hidden md:table-cell"><Skeleton variant="rectangular" width="80px" height="24px" /></td>
                      <td className="hidden lg:table-cell"><Skeleton variant="text" width="100px" /></td>
                      <td className="hidden xl:table-cell"><Skeleton variant="text" width="120px" /></td>
                      <td><Skeleton variant="rectangular" width="80px" height="24px" /></td>
                      <td className="hidden lg:table-cell"><Skeleton variant="text" width="100px" /></td>
                      <td><Skeleton variant="rectangular" width="32px" height="32px" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : error ? (
          <div className="p-12 text-center">
            <AlertTriangle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
            <p className="text-danger-600 dark:text-danger-400">خطا در بارگذاری تأمین‌کنندگان</p>
            <Button variant="outline" onClick={() => refetch()} className="mt-4">تلاش مجدد</Button>
          </div>
        ) : data?.items.length === 0 ? (
          <div className="p-12 text-center">
            <Truck className="w-16 h-16 text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">هیچ تأمین‌کننده‌ای یافت نشد</h3>
            <p className="text-neutral-500 dark:text-neutral-400 mb-4">اولین تأمین‌کننده را ایجاد کنید</p>
            <Button onClick={() => openModal('create-supplier')} disabled={!hasPermission('supplier', 'create')}>
              <Plus className="w-4 h-4 ml-1" />
              ایجاد تأمین‌کننده
            </Button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>نام</th>
                    <th className="hidden sm:table-cell">کد</th>
                    <th className="hidden md:table-cell">نوع</th>
                    <th className="hidden lg:table-cell">شهر</th>
                    <th className="hidden xl:table-cell">اطلاعات تماس</th>
                    <th>وضعیت</th>
                    <th className="hidden lg:table-cell">اعتبار</th>
                    <th className="w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {data!.items.map((supplier, index) => {
                    const statusInfo = statusConfig[supplier.status as keyof typeof statusConfig] || statusConfig.active
                    return (
                      <motion.tr
                        key={supplier.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.03 }}
                      >
                        <td className="font-medium text-neutral-900 dark:text-white">
                          {supplier.name}
                          {supplier.trade_name && <span className="ml-2 text-sm text-neutral-500 dark:text-neutral-400">({supplier.trade_name})</span>}
                        </td>
                        <td className="hidden sm:table-cell text-neutral-700 dark:text-neutral-300 font-mono">{supplier.code}</td>
                        <td className="hidden md:table-cell">
                          <Badge variant="neutral" className="text-xs">{supplier.supplier_type === 'manufacturer' ? 'تولیدکننده' : supplier.supplier_type === 'distributor' ? 'توزیع‌کننده' : supplier.supplier_type === 'wholesaler' ? 'عمده‌فروش' : 'سایر'}</Badge>
                        </td>
                        <td className="hidden lg:table-cell text-neutral-700 dark:text-neutral-300">{supplier.city || '—'}</td>
                        <td className="hidden xl:table-cell text-sm text-neutral-600 dark:text-neutral-400">
                          <div className="flex flex-col gap-1">
                            {supplier.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{supplier.email}</span>}
                            {supplier.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{supplier.phone}</span>}
                          </div>
                        </td>
                        <td>
                          <Badge variant={statusInfo.variant} className="text-xs flex items-center gap-1">
                            <statusInfo.icon className="w-3 h-3" />
                            {statusInfo.label}
                          </Badge>
                        </td>
                        <td className="hidden lg:table-cell font-mono text-sm {supplier.credit_limit > 0 ? 'text-success-600' : 'text-neutral-900'} dark:text-neutral-300">
                          {formatCurrency(supplier.credit_limit)}
                        </td>
                        <td>
                          <Dropdown
                            trigger={<button className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"><MoreVertical className="w-4 h-4" /></button>}
                            items={getRowDropdownItems(supplier)}
                          />
                        </td>
                      </motion.tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="px-5 py-4 border-t border-neutral-200 dark:border-neutral-800 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-neutral-500 dark:text-neutral-400">
                نمایش {((filters.page - 1) * filters.page_size) + 1} تا {Math.min(filters.page * filters.page_size, totalItems)} از {totalItems.toLocaleString('fa-IR')}
              </div>
              <Pagination
                currentPage={filters.page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                showPageSize
                pageSize={filters.page_size}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          </>
        )}
      </motion.div>

      {/* Create Supplier Modal */}
      <Modal
        isOpen={modals['create-supplier']}
        onClose={() => closeModal('create-supplier')}
        title="ایجاد تأمین‌کننده جدید"
        size="lg"
      >
        <SupplierForm onSubmit={createMutation.mutate} onClose={() => closeModal('create-supplier')} isLoading={createMutation.isPending} />
      </Modal>

      {/* Edit Supplier Modal */}
      <Modal
        isOpen={modals['edit-supplier']}
        onClose={() => closeModal('edit-supplier')}
        title="ویرایش تأمین‌کننده"
        size="lg"
      >
        <SupplierForm onSubmit={({ id, ...data }) => updateMutation.mutate({ id, data })} onClose={() => closeModal('edit-supplier')} isLoading={updateMutation.isPending} />
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal
        isOpen={modals['delete-confirm']}
        onClose={() => { closeModal('delete-confirm'); setDeletedId(null) }}
        title="حذف تأمین‌کننده"
        size="sm"
      >
        <p className="text-neutral-600 dark:text-neutral-400">
          آیا از حذف این تأمین‌کننده مطمئن هستید؟ این عملیات قابل بازگشت نیست.
        </p>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => { closeModal('delete-confirm'); setDeletedId(null) }}>انصراف</Button>
          <Button variant="danger" onClick={() => deletedId && deleteMutation.mutate(deletedId)} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending ? <> <Loader2 className="w-4 h-4 animate-spin" /> در حال حذف... </> : 'حذف' }
          </Button>
        </div>
      </Modal>
    </div>
  )
}

const supplierSchema = z.object({
  name: z.string().min(2, 'نام باید حداقل ۲ کاراکتر باشد'),
  trade_name: z.string().optional(),
  code: z.string().min(2, 'کد باید حداقل ۲ کاراکتر باشد'),
  supplier_type: z.enum(['manufacturer', 'distributor', 'wholesaler', 'importer', 'other']),
  tax_id: z.string().optional(),
  national_id: z.string().optional(),
  email: z.string().email('فرمت ایمیل نامعتبر است').optional().or(z.literal('')),
  phone: z.string().optional(),
  mobile: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  province: z.string().optional(),
  postal_code: z.string().optional(),
  website: z.string().url('فرمت وب‌سایت نامعتبر است').optional().or(z.literal('')),
  contact_person: z.string().optional(),
  contact_phone: z.string().optional(),
  contact_email: z.string().email('فرمت ایمیل نامعتبر است').optional().or(z.literal('')),
  payment_terms: z.number().min(0).max(365).default(30),
  credit_limit: z.number().min(0).default(0),
  currency: z.string().default('IRR'),
  status: z.enum(['active', 'inactive', 'blocked']).default('active'),
  notes: z.string().optional(),
})

type SupplierFormData = z.infer<typeof supplierSchema>

interface SupplierFormProps {
  onSubmit: (data: SupplierFormData & { id?: string }) => void
  onClose: () => void
  isLoading: boolean
}

function SupplierForm({ onSubmit, onClose, isLoading }: SupplierFormProps) {
  const methods = useForm<SupplierFormData>({
    resolver: zodResolver(supplierSchema),
    defaultValues: {
      name: '',
      trade_name: '',
      code: '',
      supplier_type: 'wholesaler',
      tax_id: '',
      national_id: '',
      email: '',
      phone: '',
      mobile: '',
      address: '',
      city: '',
      province: '',
      postal_code: '',
      website: '',
      contact_person: '',
      contact_phone: '',
      contact_email: '',
      payment_terms: 30,
      credit_limit: 0,
      currency: 'IRR',
      status: 'active',
      notes: '',
    },
  })

  const { control, handleSubmit, watch } = methods

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Controller
          name="name"
          control={control}
          rules={{ required: true, minLength: 2 }}
          render={({ field }) => (
            <Input label="نام تأمین‌کننده *" placeholder="مثال: شرکت آریان تک" icon={<Building className="w-5 h-5" />} {...field} />
          )}
        />
        <Controller
          name="trade_name"
          control={control}
          render={({ field }) => (
            <Input label="نام تجاری" placeholder="اختیاری" {...field} />
          )}
        />
        <Controller
          name="code"
          control={control}
          rules={{ required: true, minLength: 2 }}
          render={({ field }) => (
            <Input label="کد تأمین‌کننده *" placeholder="مثال: SUP-001" icon={<Building className="w-5 h-5" />} {...field} />
          )}
        />
        <Controller
          name="supplier_type"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Select
              label="نوع تأمین‌کننده *"
              options={[
                { value: 'manufacturer', label: 'تولیدکننده' },
                { value: 'distributor', label: 'توزیع‌کننده' },
                { value: 'wholesaler', label: 'عمده‌فروش' },
                { value: 'importer', label: 'واردکننده' },
                { value: 'other', label: 'سایر' },
              ]}
              placeholder="نوع تأمین‌کننده"
              {...field}
            />
          )}
        />
        <Controller
          name="tax_id"
          control={control}
          render={({ field }) => (
            <Input label="شناسه ملی / اقتصادی" placeholder="شماره ثبت/شناسه ملی" {...field} className="md:col-span-2" />
          )}
        />
        <Controller
          name="national_id"
          control={control}
          render={({ field }) => (
            <Input label="کد ملی" placeholder="اختیاری" {...field} className="md:col-span-2" />
          )}
        />
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <Input label="ایمیل" type="email" placeholder="supplier@company.com" icon={<Mail className="w-5 h-5" />} {...field} className="md:col-span-2" />
          )}
        />
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <Input label="تلفن" type="tel" placeholder="۰۲۱-۱۲۳۴۵۶۷۸" icon={<Phone className="w-5 h-5" />} {...field} />
          )}
        />
        <Controller
          name="mobile"
          control={control}
          render={({ field }) => (
            <Input label="موبایل" type="tel" placeholder="۰۹۱۲۳۴۵۶۷۸۹" icon={<Phone className="w-5 h-5" />} {...field} />
          )}
        />
        <Controller
          name="address"
          control={control}
          render={({ field }) => (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">آدرس</label>
              <textarea
                {...field}
                rows={3}
                className="w-full px-4 py-2.5 rounded-xl border bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 border-neutral-300 dark:border-neutral-700"
                placeholder="آدرس کامل تأمین‌کننده"
              />
            </div>
          )}
        />
        <Controller
          name="city"
          control={control}
          render={({ field }) => (
            <Input label="شهر" placeholder="تهران" icon={<MapPin className="w-5 h-5" />} {...field} />
          )}
        />
        <Controller
          name="province"
          control={control}
          render={({ field }) => (
            <Input label="استان" placeholder="تهران" {...field} />
          )}
        />
        <Controller
          name="postal_code"
          control={control}
          render={({ field }) => (
            <Input label="کد پستی" placeholder="۱۲۳۴۵۶۷۸۹۰" {...field} />
          )}
        />
        <Controller
          name="website"
          control={control}
          render={({ field }) => (
            <Input label="وب‌سایت" type="url" placeholder="https://supplier.com" icon={<Building className="w-5 h-5" />} {...field} className="md:col-span-2" />
          )}
        />
        <Controller
          name="contact_person"
          control={control}
          render={({ field }) => (
            <Input label="شخص تماس" placeholder="نام و نام خانوادگی" icon={<User className="w-5 h-5" />} {...field} className="md:col-span-2" />
          )}
        />
        <Controller
          name="contact_phone"
          control={control}
          render={({ field }) => (
            <Input label="تلفن تماس" type="tel" placeholder="۰۹۱۲۳۴۵۶۷۸۹" icon={<Phone className="w-5 h-5" />} {...field} />
          )}
        />
        <Controller
          name="contact_email"
          control={control}
          render={({ field }) => (
            <Input label="ایمیل تماس" type="email" placeholder="contact@supplier.com" icon={<Mail className="w-5 h-5" />} {...field} />
          )}
        />
        <Controller
          name="payment_terms"
          control={control}
          rules={{ min: 0, max: 365 }}
          render={({ field }) => (
            <Input label="شرایط پرداخت (روز)" type="number" min={0} max={365} step={1} defaultValue={30} {...field} />
          )}
        />
        <Controller
          name="credit_limit"
          control={control}
          rules={{ min: 0 }}
          render={({ field }) => (
            <Input label="محدودیت اعتبار (ریال)" type="number" min={0} step={1000000} defaultValue={0} {...field} />
          )}
        />
        <Controller
          name="currency"
          control={control}
          render={({ field }) => (
            <Input label="واحد پول" defaultValue="IRR" {...field} />
          )}
        />
        <Controller
          name="status"
          control={control}
          render={({ field }) => (
            <Select
              label="وضعیت"
              options={[
                { value: 'active', label: 'فعال' },
                { value: 'inactive', label: 'غیرفعال' },
                { value: 'blocked', label: 'مسدود' },
              ]}
              placeholder="وضعیت"
              {...field}
            />
          )}
        />
        <Controller
          name="notes"
          control={control}
          render={({ field }) => (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">یادداشت‌ها</label>
              <textarea
                {...field}
                rows={3}
                className="w-full px-4 py-2.5 rounded-xl border bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 border-neutral-300 dark:border-neutral-700"
                placeholder="یادداشت‌های داخلی درباره این تأمین‌کننده..."
              />
            </div>
          )}
        />
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-800">
        <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>انصراف</Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? <> <Loader2 className="w-4 h-4 animate-spin" /> در حال ثبت... </> : <> <Shield className="w-4 h-4 ml-1" /> ثبت تأمین‌کننده </> }
        </Button>
      </div>
    </form>
  )
}

type SupplierListParams = {
  page: number
  page_size: number
  sort_by: string
  sort_order: 'asc' | 'desc'
  search?: string
  status?: string
  supplier_type?: string
  city?: string
  date_from?: string
  date_to?: string
}